import { UUID } from 'node:crypto';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ITerminal, TerminalAdministrationService } from './terminalAdministration.service';
import { ITerminalTemplate, TerminalTemplateAdministrationService } from './terminaltemplateAdministration.service';
import { UserService } from './user.service';

@Injectable({
	providedIn: 'root',
})
export class ProductionTerminalService {
	constructor(
		private terminalTemplateService: TerminalTemplateAdministrationService,
		private terminalService: TerminalAdministrationService,
		private userService: UserService,
	) {
		this.userService.accountChanged$.subscribe(() => this.setTerminalId(null));
	}

	private _terminalId: UUID;
	private _terminal: ITerminal;
	private _terminalTemplate: ITerminalTemplate;
	// Bumped on every fetch/direct-update so a stale in-flight fetch can detect
	// it's been superseded (by a terminal switch OR a direct applyTerminalUpdate)
	// and discard its result instead of overwriting newer data.
	private _terminalVersion = 0;

	private _terminalIdSubject: BehaviorSubject<UUID> = new BehaviorSubject<UUID>(null);
	private _terminalSubject: BehaviorSubject<ITerminal> = new BehaviorSubject<ITerminal>(null);
	private _terminalTemplateSubject: BehaviorSubject<ITerminalTemplate> = new BehaviorSubject<ITerminalTemplate>(null);

	public get terminalId(): UUID {
		return this._terminalId;
	}

	public get terminal(): ITerminal {
		return this._terminal;
	}

	public get terminalTemplate(): ITerminalTemplate {
		return this._terminalTemplate;
	}

	public setTerminalId(value: UUID | undefined) {
		if (this._terminalId === value) {
			return;
		}
		// Reset the itemoperations when not provided a valid id
		// In this case we're most likely accessing the 'komme/gå' terminal
		if (!value) {
			this._terminalVersion++;
			this._terminalId = null;
			this._terminalIdSubject.next(null);
			this._terminal = null;
			this._terminalTemplate = null;
			this._terminalSubject.next(null);
			this._terminalTemplateSubject.next(null);
			return;
		}

		// New terminal
		this._terminalId = value;
		this._terminalIdSubject.next(value);
		this._terminal = null;
		// Clear the previous terminal's data immediately so subscribers (e.g. the
		// print menu) don't keep exposing the outgoing terminal's printConfigurations
		// while the new terminal is still loading.
		this._terminalSubject.next(null);
		this._terminalTemplate = null;
		this._terminalTemplateSubject.next(null);
		const version = ++this._terminalVersion;
		this.terminalService.getTerminalById(value).subscribe((t) => {
			// Discard if superseded by a later setTerminalId/applyTerminalUpdate call while this was in flight
			if (this._terminalId === value && this._terminalVersion === version) {
				this.setTerminal(t);
			}
		});
	}

	onTerminalIdChanged(): Observable<UUID> {
		return this._terminalIdSubject.asObservable();
	}

	// Called after a create/update terminal save so an already-loaded production
	// terminal reflects changes (e.g. printConfigurations) without a re-fetch.
	public applyTerminalUpdate(terminal: ITerminal): void {
		if (this._terminalId === terminal.id) {
			this._terminalVersion++;
			this.setTerminal(terminal);
		}
	}

	onTerminalChanged(): Observable<ITerminal> {
		return this._terminalSubject.asObservable();
	}

	onTerminalTemplateChanged(): Observable<ITerminalTemplate> {
		return this._terminalTemplateSubject.asObservable();
	}

	private setTerminal(terminal: ITerminal) {
		if (this._terminal === terminal) return;
		this._terminal = terminal;
		this._terminalSubject.next(terminal);
		this._terminalTemplate = null;
		// Capture the version associated with this terminal so a template response
		// can be discarded if a later setTerminal call (switch or direct update)
		// changes terminalTemplateId before this request resolves.
		const version = this._terminalVersion;
		this.terminalTemplateService.getTerminalTemplateById(terminal.terminalTemplateId).subscribe((tt) => {
			if (this._terminalVersion === version) {
				this.setTerminalTemplate(tt);
			}
		});
	}

	private setTerminalTemplate(terminalTemplate: ITerminalTemplate) {
		if (this._terminalTemplate === terminalTemplate) return;
		this._terminalTemplate = terminalTemplate;
		this._terminalTemplateSubject.next(terminalTemplate);
	}
}
